import React from 'react';
import {
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesIconButton,
  AgnesPageHeader,
} from '@agnes/ui';
import { Download, History, Pencil, Plus, Table2 } from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import { usePermissionsStore } from '@/stores/usePermissionsStore';
import { getIncidenciaStore, type LinhaIncidencia } from '@/stores/incidenciaStoreFactory';
import type { IncidenciaSpec } from '@/lib/incidenciaSpecs';
import { SearchInput } from '@/components/SearchInput';
import ModalIncidencia from './ModalIncidencia';
import DrawerIncidenciaHistorico from './DrawerIncidenciaHistorico';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

export default function PageTabelaIncidencia({ spec }: { spec: IncidenciaSpec }) {
  const useStore = React.useMemo(() => getIncidenciaStore(spec.key), [spec.key]);
  const lista = useStore((s) => s.lista) as LinhaIncidencia[];
  const total = useStore((s) => s.total);
  const page = useStore((s) => s.page);
  const size = useStore((s) => s.size);
  const carregando = useStore((s) => s.carregando);
  const exportando = useStore((s) => s.exportando);
  const busca = useStore((s) => s.busca);
  const sortBy = useStore((s) => s.sortBy);
  const sortOrder = useStore((s) => s.sortOrder);
  const podeEditar = usePermissionsStore((s) => s.hasPermission(spec.permWrite));

  const [modalOpen, setModalOpen] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [linhaSel, setLinhaSel] = React.useState<LinhaIncidencia | null>(null);

  React.useEffect(() => {
    useStore.getState().buscar();
  }, [useStore]);

  const abrirNovo = () => {
    useStore.getState().setEditando(null);
    setLinhaSel(null);
    setModalOpen(true);
  };
  const abrirEdicao = (r: LinhaIncidencia) => {
    useStore.getState().setEditando(r.code);
    setLinhaSel(r);
    setModalOpen(true);
  };
  const fecharModal = () => {
    useStore.getState().setEditando(null);
    setLinhaSel(null);
    setModalOpen(false);
  };
  const abrirHistorico = (r: LinhaIncidencia) => {
    useStore
      .getState()
      .carregarHistorico(r.code)
      .then(() => setDrawerOpen(true));
  };

  const columns: AgnesDataTableColumn<LinhaIncidencia>[] = [
    { key: 'code', header: 'Código', width: 120, render: (r) => r.code },
    { key: 'name', header: 'Nome', width: 380, render: (r) => r.name },
    ...spec.colunasExtra.map((c) => ({
      key: c.key,
      header: c.header,
      width: c.width ?? 160,
      render: (r: LinhaIncidencia) => {
        const v = (r as any)[c.key];
        return (c.format ? c.format(v) : (v ?? '-')) as React.ReactNode;
      },
    })),
    { key: 'notes', header: 'Observações', width: 220, render: (r) => r.notes ?? '-' },
    { key: 'updatedAt', header: 'Atualização', width: 170, render: (r) => r.updatedAt ?? '-' },
    { key: 'updatedBy', header: 'Atualizado por', width: 170, render: (r) => r.updatedBy ?? '-' },
    {
      key: 'acoes',
      header: 'Ações',
      width: 110,
      align: 'center',
      render: (r) => (
        <div className="flex items-center justify-center gap-1">
          {podeEditar && (
            <AgnesIconButton
              aria-label="Editar"
              size="sm"
              title="Editar"
              onClick={() => abrirEdicao(r)}
            >
              <Pencil className="h-4 w-4" />
            </AgnesIconButton>
          )}
          <AgnesIconButton
            aria-label="Histórico"
            size="sm"
            title="Histórico"
            onClick={() => abrirHistorico(r)}
          >
            <History className="h-4 w-4" />
          </AgnesIconButton>
        </div>
      ),
    },
  ];

  // CON-5188: todas as colunas de dado (code/name/extras/notes/updatedAt/updatedBy) têm
  // alias no backend (search?sort=); só `acoes` é estrutural.
  const cols = columns.map((c) => (c.key === 'acoes' ? c : { ...c, sortable: true }));

  return (
    <>
      <AgnesPageHeader
        icon={<Table2 />}
        title={spec.titulo}
        subtitle={spec.subtitulo}
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Configurações' },
          { label: 'eSocial' },
          { label: 'Tabelas', href: '/admin/tabelas' },
          { label: spec.titulo },
        ]}
      />

      <AgnesCard>
        <AgnesCardContent className="flex items-stretch">
          <div className="mb-3 flex items-center gap-2">
            <SearchInput
              value={busca}
              onValueChange={(v) => useStore.getState().setBusca(v)}
              onSearch={() => useStore.getState().buscar()}
              placeholder="Buscar por código ou nome"
            />
            <div className="ml-auto flex gap-2">
              <ExportButton
                variant="outlined"
                startIcon={<Download className="h-4 w-4" />}
                disabled={exportando}
                onClick={() => useStore.getState().exportar()}
              >
                Exportar
              </ExportButton>
              {podeEditar && (
                <AgnesButton
                  variant="primary"
                  startIcon={<Plus className="h-4 w-4" />}
                  onClick={abrirNovo}
                >
                  Nova incidência
                </AgnesButton>
              )}
            </div>
          </div>

          <TableWrapper>
            <AgnesDataTable<LinhaIncidencia>
              columns={cols}
              data={lista}
              rowKey={(r) => r.code}
              loading={carregando}
              sort={{
                sortBy,
                sortOrder,
                onSortChange: (by, order) => useStore.getState().setSort(by, order),
              }}
              pagination={{
                page,
                pageSize: size,
                total,
                serverSide: true,
                onPageChange: (p: number) => useStore.getState().setPage(p),
                onPageSizeChange: (s: number) => useStore.getState().setSize(s),
              }}
            />
          </TableWrapper>
        </AgnesCardContent>
      </AgnesCard>

      <ModalIncidencia
        open={modalOpen}
        onClose={fecharModal}
        linha={linhaSel}
        spec={spec}
        useStore={useStore}
      />

      <DrawerIncidenciaHistorico
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        useStore={useStore}
      />
    </>
  );
}
